import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, 
  FileText, 
  TrendingUp,
  Calendar,
  Plus,
  Search,
  Send,
  Eye,
  EyeOff,
  Edit,
  CheckCircle,
  AlertCircle,
  Clock,
  DollarSign,
  Building2,
  UserCheck,
  Mail,
  Download,
  CreditCard,
  ChevronDown,
  ChevronRight,
  X,
  Receipt,
  Trash2,
  ExternalLink,
  BadgeCheck,
  Banknote,
  QrCode
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import MonthlySubscriberForm from '../../components/forms/MonthlySubscriberForm';
import MonthlyPlanForm from '../../components/forms/MonthlyPlanForm';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import { asaasHelpers } from '../../lib/asaasHelpers';
import toast from 'react-hot-toast';

const MonthlyPaymentsPage = () => {
  const { company } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResponsavel, setSelectedResponsavel] = useState(null);
  const [expandedCards, setExpandedCards] = useState({});
  const [showSubscriberModal, setShowSubscriberModal] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [showInvoicesModal, setShowInvoicesModal] = useState(false);
  const [selectedStudentInvoices, setSelectedStudentInvoices] = useState(null);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);  const [showPlansManager, setShowPlansManager] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Estados dos dados
  const [data, setData] = useState({
    subscribers: [],
    invoices: [],
    plans: []
  });

  // Dados organizados por responsável
  const responsaveisData = useMemo(() => {
    const grouped = {};
    
    data.subscribers.forEach(subscriber => {
      const payerKey = subscriber.payer_email || subscriber.payer_name;
      if (!grouped[payerKey]) {
        grouped[payerKey] = {
          payer_name: subscriber.payer_name,
          payer_email: subscriber.payer_email,
          payer_phone: subscriber.payer_phone,
          payer_cpf: subscriber.payer_cpf,
          asaas_customer_id: subscriber.asaas_customer_id,
          students: [],
          total_monthly: 0,
          active_count: 0
        };
      }
      
      // Buscar faturas do aluno
      const studentInvoices = data.invoices.filter(inv => 
        inv.monthly_subscriber_id === subscriber.id
      );
      
      const studentData = {
        ...subscriber,
        invoices: studentInvoices,
        pending_invoices: studentInvoices.filter(inv => inv.status === 'pending').length,
        overdue_invoices: studentInvoices.filter(inv => inv.status === 'overdue').length,
        paid_invoices: studentInvoices.filter(inv => inv.status === 'paid').length
      };
      
      grouped[payerKey].students.push(studentData);
      
      if (subscriber.status === 'active') {
        grouped[payerKey].active_count++;
        grouped[payerKey].total_monthly += parseFloat(subscriber.monthly_plans?.monthly_value || 0);
      }
    });
    
    return Object.values(grouped);
  }, [data.subscribers, data.invoices]);

  // Filtrar responsáveis
  const filteredResponsaveis = useMemo(() => {
    if (!searchTerm) return responsaveisData;
    
    return responsaveisData.filter(resp => 
      resp.payer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resp.payer_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resp.students.some(student => 
        student.students?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.students?.registration_number?.includes(searchTerm)
      )
    );
  }, [responsaveisData, searchTerm]);

  // Métricas consolidadas
  const metrics = useMemo(() => {
    const totalResponsaveis = responsaveisData.length;
    const totalAlunos = data.subscribers.length;
    const alunosAtivos = data.subscribers.filter(s => s.status === 'active').length;
    
    const faturasPendentes = data.invoices.filter(inv => inv.status === 'pending').length;
    const faturasVencidas = data.invoices.filter(inv => inv.status === 'overdue').length;
    
    const receitaMensal = responsaveisData.reduce((sum, resp) => sum + resp.total_monthly, 0);
    
    // Calcular valores pendentes e vencidos
    const valorPendente = data.invoices
      .filter(inv => inv.status === 'pending')
      .reduce((sum, inv) => sum + parseFloat(inv.final_amount || 0), 0);
    
    const valorVencido = data.invoices
      .filter(inv => inv.status === 'overdue')
      .reduce((sum, inv) => sum + parseFloat(inv.final_amount || 0), 0);
    
    const valorTotalPendente = valorPendente + valorVencido;
    
    return {
      totalResponsaveis,
      totalAlunos,
      alunosAtivos,
      faturasPendentes,
      faturasVencidas,
      receitaMensal,
      valorPendente,
      valorVencido,
      valorTotalPendente
    };
  }, [responsaveisData, data.subscribers, data.invoices]);

  useEffect(() => {
    loadData();
  }, [company]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [subscribersData, invoicesData, plansData] = await Promise.all([
        dataHelpers.getMonthlySubscribers(company.id),
        dataHelpers.getMonthlyInvoices(company.id),
        dataHelpers.getMonthlyPlans(company.id)
      ]);
      
      console.log('Loaded data:', { subscribersData, invoicesData, plansData });
      
      setData({
        subscribers: subscribersData || [],
        invoices: invoicesData || [],
        plans: plansData || []
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const toggleCardExpansion = (payerEmail) => {
    setExpandedCards(prev => ({
      ...prev,
      [payerEmail]: !prev[payerEmail]
    }));
  };

  const getStatusConfig = (status) => {
    const configs = {
      paid: { color: 'text-green-600 bg-green-100', text: 'Pago', icon: CheckCircle },
      pending: { color: 'text-yellow-600 bg-yellow-100', text: 'Pendente', icon: Clock },
      overdue: { color: 'text-red-600 bg-red-100', text: 'Vencido', icon: AlertCircle },
      cancelled: { color: 'text-gray-600 bg-gray-100', text: 'Cancelado', icon: AlertCircle },
      active: { color: 'text-green-600 bg-green-100', text: 'Ativo', icon: CheckCircle },
      suspended: { color: 'text-yellow-600 bg-yellow-100', text: 'Suspenso', icon: Clock },
      inactive: { color: 'text-gray-600 bg-gray-100', text: 'Inativo', icon: AlertCircle }
    };
    return configs[status] || configs.inactive;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('pt-BR');
  };
  const renderViewedIcon = (invoice) => {
    if (!invoice.viewed_at) {
      return (
        <span title="Fatura não visualizada" className="text-gray-400">
          <EyeOff className="w-5 h-5" />
        </span>
      );
    }
    
    return (
      <span title={`Visualizada em: ${formatDateTime(invoice.viewed_at)}`} className="text-green-600">
        <Eye className="w-5 h-5" />
      </span>
    );
  };
  const renderPaymentMethodIcon = (paymentMethod) => {
    if (!paymentMethod) return null;
    
    const methods = {
      'boleto': { icon: Receipt, color: 'text-orange-600', title: 'Boleto Bancário' },
      'pix': { icon: QrCode, color: 'text-blue-600', title: 'PIX' },
      'credit_card': { icon: CreditCard, color: 'text-purple-600', title: 'Cartão de Crédito' },
      'debit_card': { icon: CreditCard, color: 'text-indigo-600', title: 'Cartão de Débito' },
      'transfer': { icon: Banknote, color: 'text-green-600', title: 'Transferência' }
    };
    
    const method = methods[paymentMethod] || { icon: DollarSign, color: 'text-gray-600', title: 'Outro' };
    const Icon = method.icon;
    
    return (
      <span title={method.title} className={method.color}>
        <Icon className="w-5 h-5" />
      </span>
    );
  };
  const renderPaymentStatus = (invoice) => {
    if (invoice.status === 'paid' && invoice.paid_at) {
      return (
        <div className="flex items-center space-x-1">
          <span title={`Pago em: ${formatDateTime(invoice.paid_at)}`} className="text-green-600">
            <BadgeCheck className="w-5 h-5" />
          </span>
          {renderPaymentMethodIcon(invoice.payment_method)}
        </div>
      );
    }
    
    return null;
  };

  const confirmDelete = async () => {
    if (deleteTarget?.confirmAction) {
      await deleteTarget.confirmAction();
    }
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const openInvoiceDetails = (invoice) => {
    if (invoice.asaas_invoice_url) {
      window.open(invoice.asaas_invoice_url, '_blank');
    } else {
      toast.error('URL da fatura não disponível');
    }
  };



  const sendNotification = async (invoiceId, type = 'email') => {
    try {
      // Implementar envio de notificação
      toast.success('Notificação enviada com sucesso!');
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Erro ao enviar notificação');
    }
  };

  const handleEditSubscriber = (subscriber) => {
    setEditingSubscriber(subscriber);
    setShowSubscriberModal(true);
  };

  const handleViewInvoices = (student) => {
    setSelectedStudentInvoices(student);
    setShowInvoicesModal(true);
  };

  const handleSendNotification = async (student) => {
    try {
      const pendingInvoices = student.invoices.filter(inv => 
        inv.status === 'pending' || inv.status === 'overdue'
      );
      
      if (pendingInvoices.length === 0) {
        toast.success('Não há faturas pendentes para este aluno');
        return;
      }

      // Aqui implementaríamos o envio real via Asaas
      await Promise.all(pendingInvoices.map(invoice => 
        sendNotification(invoice.id, 'email')
      ));
      
      toast.success(`Notificações enviadas para ${student.students?.name}`);
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error('Erro ao enviar notificações');
    }
  };

  const handleExportReport = async () => {
    try {
      // Implementar geração de relatório
      const reportData = {
        responsaveis: responsaveisData,
        metrics,
        periodo: new Date().toISOString().slice(0, 7)
      };
      
      // Simular download do relatório
      const dataStr = JSON.stringify(reportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-mensalidades-${new Date().toISOString().slice(0, 7)}.json`;
      link.click();
      
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Erro ao gerar relatório');
    }
  };

  const handleSubscriberSubmit = async (formData) => {
    try {
      let newSubscriber;
      
      if (editingSubscriber) {
        await dataHelpers.updateMonthlySubscriber(editingSubscriber.id, formData);
        toast.success('Assinatura atualizada com sucesso!');
      } else {
        // Criar nova assinatura
        const loadingToast = toast.loading('Criando assinatura e gerando faturas...');
        
        try {
          newSubscriber = await dataHelpers.createMonthlySubscriber(company.id, formData);
          
          // SEMPRE gerar automaticamente todas as parcelas para novas assinaturas
          if (newSubscriber && formData.monthly_plan_id) {
            await generateAllInstallments(newSubscriber, formData.monthly_plan_id);
          }
          
          toast.dismiss(loadingToast);
          toast.success('Assinatura criada e todas as faturas geradas com sucesso!');
        } catch (error) {
          toast.dismiss(loadingToast);
          throw error;
        }
      }
      
      setShowSubscriberModal(false);
      setEditingSubscriber(null);
      await loadData();
    } catch (error) {
      console.error('Error saving subscriber:', error);
      toast.error('Erro ao salvar assinatura: ' + error.message);
    }
  };

  const generateAllInstallments = async (subscriber, planId) => {
    try {
      // Buscar dados do plano
      const plan = data.plans.find(p => p.id === planId);
      if (!plan) {
        throw new Error('Plano não encontrado');
      }

      const installments = plan.installments || 1;
      const startDate = new Date(subscriber.start_date);
      const dueDay = plan.due_day || 10;

      console.log(`📋 Gerando ${installments} faturas para assinatura ${subscriber.id}`);
      console.log(`📅 Plano: dia de vencimento = ${dueDay}`);

      // Preparar todas as faturas
      const invoicesToCreate = [];
      
      // Calcular primeira data de vencimento baseada no due_day do plano
      const today = new Date();
      const currentDay = today.getDate();
      
      console.log(`📆 Hoje: ${today.toLocaleDateString('pt-BR')} (dia ${currentDay})`);
      
      // Se hoje é antes do dia de vencimento, primeira fatura vence no mês atual
      // Se hoje é depois do dia de vencimento, primeira fatura vence no próximo mês
      let firstDueDate;
      if (currentDay < dueDay) {
        // Primeiro vencimento no mês atual
        firstDueDate = new Date(today.getFullYear(), today.getMonth(), dueDay);
        console.log(`✅ Dia atual (${currentDay}) < dia vencimento (${dueDay}) → primeira fatura no mês atual`);
      } else {
        // Primeiro vencimento no próximo mês
        firstDueDate = new Date(today.getFullYear(), today.getMonth() + 1, dueDay);
        console.log(`✅ Dia atual (${currentDay}) >= dia vencimento (${dueDay}) → primeira fatura no próximo mês`);
      }
      
      console.log(`🎯 Primeira data de vencimento: ${firstDueDate.toLocaleDateString('pt-BR')}`);
      
      for (let i = 1; i <= installments; i++) {
        // Calcular data de vencimento: primeira data + (i-1) meses
        const dueDate = new Date(
          firstDueDate.getFullYear(),
          firstDueDate.getMonth() + (i - 1),
          dueDay
        );

        // Data de referência baseada na data de vencimento (não na data de início)
        const referenceDate = new Date(
          dueDate.getFullYear(),
          dueDate.getMonth(),
          1
        );
        
        console.log(`💰 Parcela ${i}/${installments}: venc=${dueDate.toLocaleDateString('pt-BR')}, ref=${referenceDate.toLocaleDateString('pt-BR')}`);

        const invoiceData = {
          monthly_subscriber_id: subscriber.id,
          company_id: company.id,
          monthly_plan_id: planId,
          reference_month: referenceDate.toISOString().split('T')[0],
          due_date: dueDate.toISOString().split('T')[0],
          original_amount: plan.monthly_value,
          final_amount: plan.monthly_value,
          installment_number: i,
          total_installments: installments,
          status: 'pending'
        };

        invoicesToCreate.push(invoiceData);
      }

      // Criar todas as faturas
      for (const invoiceData of invoicesToCreate) {
        await dataHelpers.createMonthlyInvoice(company.id, invoiceData);
      }

      console.log(`✅ ${installments} faturas geradas com sucesso para assinatura ${subscriber.id}`);
      
    } catch (error) {
      console.error('Error generating installments:', error);
      throw new Error(`Erro ao gerar faturas: ${error.message}`);
    }
  };

  // Função para testar cálculo de datas (apenas para debug)
  const testDateCalculation = (dueDay) => {
    const today = new Date();
    const currentDay = today.getDate();
    
    console.log(`🧪 TESTE: Plano com vencimento dia ${dueDay}`);
    console.log(`📅 Hoje: ${today.toLocaleDateString('pt-BR')} (dia ${currentDay})`);
    
    let firstDueDate;
    if (currentDay < dueDay) {
      firstDueDate = new Date(today.getFullYear(), today.getMonth(), dueDay);
      console.log(`✅ ${currentDay} < ${dueDay} → Primeira fatura: ${firstDueDate.toLocaleDateString('pt-BR')} (mês atual)`);
    } else {
      firstDueDate = new Date(today.getFullYear(), today.getMonth() + 1, dueDay);
      console.log(`✅ ${currentDay} >= ${dueDay} → Primeira fatura: ${firstDueDate.toLocaleDateString('pt-BR')} (próximo mês)`);
    }
    
    // Mostrar próximas 3 faturas
    for (let i = 1; i <= 3; i++) {
      const dueDate = new Date(
        firstDueDate.getFullYear(),
        firstDueDate.getMonth() + (i - 1),
        dueDay
      );
      console.log(`📋 Parcela ${i}: ${dueDate.toLocaleDateString('pt-BR')}`);
    }
  };

  const handlePlanSubmit = async (formData) => {
    try {
      if (editingPlan) {
        await dataHelpers.updateMonthlyPlan(editingPlan.id, formData);
        toast.success('Plano atualizado com sucesso!');
      } else {
        await dataHelpers.createMonthlyPlan(company.id, formData);
        toast.success('Plano criado com sucesso!');
      }
      
      setShowPlanModal(false);
      setEditingPlan(null);
      await loadData();
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error('Erro ao salvar plano');
    }
  };
  const handleDeletePlan = async (planId) => {
    const plan = data.plans.find(p => p.id === planId);
    setDeleteTarget({
      type: 'plan',
      id: planId,
      name: plan?.name,
      confirmAction: async () => {
        try {
          await dataHelpers.deleteMonthlyPlan(planId);
          toast.success('Plano excluído com sucesso!');
          await loadData();
        } catch (error) {
          console.error('Error deleting plan:', error);
          toast.error('Erro ao excluir plano');
        }
      }
    });
    setShowDeleteModal(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setShowPlanModal(true);
  };
  const handleDeleteSubscriber = async (subscriber) => {
    try {
      setLoading(true);
      
      // Buscar todas as faturas da assinatura
      const subscriberInvoices = data.invoices.filter(inv => 
        inv.monthly_subscriber_id === subscriber.id
      );
      
      console.log('📋 Validando exclusão da assinatura:', {
        subscriber_id: subscriber.id,
        student: subscriber.students?.name,
        total_invoices: subscriberInvoices.length
      });
      
      // 🚫 VALIDAÇÃO 1: Verificar se há faturas pagas
      const paidInvoices = subscriberInvoices.filter(inv => inv.status === 'paid');
      
      if (paidInvoices.length > 0) {
        setLoading(false);
        toast.error(`❌ Não é possível excluir! Esta assinatura possui ${paidInvoices.length} fatura(s) já paga(s). Para excluir, cancele os pagamentos primeiro.`);
        return;
      }
      
      // 📊 Análise das faturas para o modal de confirmação
      const integratedInvoices = subscriberInvoices.filter(inv => inv.asaas_payment_id);
      const pendingInvoices = subscriberInvoices.filter(inv => inv.status === 'pending');
      
      let warningMessage = 'Esta ação irá excluir todas as faturas relacionadas e não pode ser desfeita.';
      
      if (integratedInvoices.length > 0) {
        warningMessage += `\n\n⚠️ ATENÇÃO: ${integratedInvoices.length} fatura(s) serão excluídas também do Asaas.`;
      }
      
      if (pendingInvoices.length > 0) {
        warningMessage += `\n\n📋 Faturas pendentes que serão excluídas: ${pendingInvoices.length}`;
      }
      
      setLoading(false);
      
      // Configurar modal de confirmação com validações
      setDeleteTarget({
        type: 'subscriber',
        id: subscriber.id,
        name: subscriber.students?.name,
        warning: warningMessage,
        confirmAction: async () => {
          try {
            setLoading(true);
            
            const loadingToast = toast.loading('Excluindo assinatura e faturas...');
            
            // 🗑️ ETAPA 1: Excluir faturas integradas do Asaas
            if (integratedInvoices.length > 0) {
              console.log(`🔗 Excluindo ${integratedInvoices.length} faturas do Asaas...`);
              
              let asaasDeleteCount = 0;
              let asaasErrorCount = 0;
              
              for (const invoice of integratedInvoices) {
                try {
                  console.log('🔍 Tentando excluir do Asaas:', { 
                    payment_id: invoice.asaas_payment_id, 
                    company_id: company.id,
                    invoice_number: invoice.installment_number 
                  });
                  
                  const deleteResult = await asaasHelpers.deletePayment(invoice.asaas_payment_id, company.id);
                  
                  if (deleteResult.success) {
                    asaasDeleteCount++;
                    console.log(`✅ Fatura ${invoice.installment_number} excluída do Asaas`);
                  } else {
                    asaasErrorCount++;
                    console.warn(`⚠️ Erro ao excluir fatura ${invoice.installment_number} do Asaas:`, deleteResult.error);
                  }
                } catch (error) {
                  asaasErrorCount++;
                  console.error(`❌ Erro ao excluir fatura ${invoice.installment_number} do Asaas:`, error);
                }
              }
              
              if (asaasErrorCount > 0) {
                console.warn(`⚠️ ${asaasErrorCount} de ${integratedInvoices.length} faturas não foram excluídas do Asaas`);
              }
              
              console.log(`📊 Resultado da exclusão no Asaas: ${asaasDeleteCount} sucesso, ${asaasErrorCount} erros`);
            }
            
            // 🗑️ ETAPA 2: Excluir assinatura do sistema local (isso vai excluir as faturas por cascade)
            await dataHelpers.deleteMonthlySubscriber(company.id, subscriber.id);
            
            toast.dismiss(loadingToast);
            
            let successMessage = `✅ Assinatura de ${subscriber.students?.name} excluída com sucesso!`;
            
            if (integratedInvoices.length > 0) {
              successMessage += `\n📋 ${integratedInvoices.length} fatura(s) também foram excluídas do Asaas.`;
            }
            
            toast.success(successMessage);
            
            // Recarregar dados
            await loadData();
            
          } catch (error) {
            console.error('Error deleting subscriber:', error);
            toast.error('Erro ao excluir assinatura: ' + error.message);
          } finally {
            setLoading(false);
          }
        }
      });
      
      setShowDeleteModal(true);
      
    } catch (error) {
      console.error('Error validating subscriber deletion:', error);
      toast.error('Erro ao validar exclusão da assinatura');
      setLoading(false);
    }
  };

  const handleIntegrateInvoiceWithAsaas = async (invoice, student) => {
    try {
      setLoading(true);
      
      console.log('🔗 Integrando fatura individual:', invoice.id);
      
      // Verificar se a fatura já está integrada
      if (invoice.asaas_payment_id) {
        toast.success('Fatura já está integrada com Asaas');
        return;
      }
      
      // Verificar se os dados do responsável estão preenchidos
      if (!student.payer_name || !student.payer_email || !student.payer_cpf) {
        toast.error('Dados do responsável incompletos. Preencha nome, email e CPF na assinatura.');
        return;
      }
      
      const loadingToast = toast.loading('Integrando fatura com Asaas...');
      
      try {
        // Buscar customer_id direto da tabela people usando selected_parent_id
        let customerId = null;
        
        if (student.selected_parent_id) {
          try {
            console.log('Buscando customer_id do responsável na tabela people...');
            const responsiblePerson = await dataHelpers.getPerson(student.selected_parent_id);
            
            if (responsiblePerson && responsiblePerson.asaas_customer_id) {
              customerId = responsiblePerson.asaas_customer_id;
              console.log('Customer_id encontrado na tabela people:', customerId);
            } else {
              console.log('Responsável não possui asaas_customer_id na tabela people');
            }
          } catch (error) {
            console.error('Erro ao buscar dados do responsável:', error);
          }
        } else {
          console.log('Assinatura não possui selected_parent_id');
        }
        
        // Garantir que o responsável financeiro seja cliente do Asaas
        if (!customerId) {
          console.log('🔍 Responsável ainda não é cliente do Asaas, verificando...');
          
          try {
            // Buscar dados completos do responsável financeiro
            const responsiblePersonData = {
              id: student.selected_parent_id,
              name: student.payer_name,
              email: student.payer_email,
              cpf: student.payer_cpf,
              document: student.payer_cpf,
              phone: student.payer_phone,
              asaas_customer_id: null // Será verificado pela função
            };

            const customerResult = await asaasHelpers.ensureCustomerExists(responsiblePersonData, company.id);
            
            customerId = customerResult.customer_id;
            
            if (customerResult.created_new) {
              console.log('✅ Novo cliente criado no Asaas:', customerId);
              toast.success('Cliente criado no Asaas com sucesso!');
            } else {
              console.log('✅ Cliente existente encontrado:', customerId);
            }
            
          } catch (customerError) {
            toast.dismiss(loadingToast);
            toast.error(`Erro ao garantir cliente no Asaas: ${customerError.message}`);
            return;
          }
        }
        
        // Preparar dados da cobrança individual
        const paymentData = {
          customer_id: customerId,
          company_id: company.id,
          value: parseFloat(invoice.final_amount),
          description: `${student.monthly_plans?.name || 'Mensalidade'} - ${student.students?.name || student.payer_name} - Parcela ${invoice.installment_number}/${invoice.total_installments}`,
          due_date: invoice.due_date,
          postal_service: false
        };
        
        // Criar cobrança individual no Asaas com retry automático
        const responsiblePersonData = {
          id: student.selected_parent_id,
          name: student.payer_name,
          email: student.payer_email,
          cpf: student.payer_cpf,
          phone: student.payer_phone
        };
        
        const paymentResult = await asaasHelpers.createBoletoPaymentWithRetry(
          paymentData, 
          responsiblePersonData, 
          company.id
        );
        
        // Atualizar fatura com dados do Asaas
        if (paymentResult.success && paymentResult.payment) {
          await dataHelpers.updateMonthlyInvoice(invoice.id, {
            asaas_payment_id: paymentResult.payment.id,
            asaas_invoice_url: paymentResult.invoice_url,
            asaas_status: paymentResult.payment.status
          });
          
          toast.dismiss(loadingToast);
          toast.success(`Fatura ${invoice.installment_number}/${invoice.total_installments} integrada com sucesso!`);
          console.log(`Fatura integrada com sucesso:`, paymentResult.payment.id);
          
          // Recarregar dados
          await loadData();
          
          // Atualizar faturas no modal
          if (student.id) {
            const data = await dataHelpers.getMonthlyInvoices(company.id);
            const subscriberInvoices = data.filter(inv => 
              inv.monthly_subscriber_id === student.id
            );
            setSelectedStudentInvoices(prev => ({
              ...prev,
              invoices: subscriberInvoices
            }));
          }
        } else {
          throw new Error(paymentResult.error || 'Erro na resposta do Asaas');
        }
      } catch (error) {
        toast.dismiss(loadingToast);
        throw error;
      }
    } catch (error) {
      console.error('Error integrating invoice with Asaas:', error);
      toast.error('Erro ao integrar fatura: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleIntegrateWithAsaas = async (student) => {
    try {
      setLoading(true);
      
      // Buscar faturas pendentes do aluno
      const pendingInvoices = student.invoices.filter(inv => 
        inv.status === 'pending' && !inv.asaas_payment_id
      );
      
      if (pendingInvoices.length === 0) {
        toast.success('Não há faturas pendentes sem integração Asaas para este aluno');
        return;
      }

      const loadingToast = toast.loading(`Integrando ${pendingInvoices.length} faturas com Asaas...`);
      
      // Verificar se os dados do responsável estão preenchidos
      if (!student.payer_name || !student.payer_email || !student.payer_cpf) {
        toast.dismiss(loadingToast);
        toast.error('Dados do responsável incompletos. Preencha nome, email e CPF na assinatura.');
        return;
      }
      
      // Buscar customer_id direto da tabela people usando selected_parent_id
      let customerId = null;
      
      if (student.selected_parent_id) {
        try {
          console.log('Buscando customer_id do responsável na tabela people...');
          const responsiblePerson = await dataHelpers.getPerson(student.selected_parent_id);
          
          if (responsiblePerson && responsiblePerson.asaas_customer_id) {
            customerId = responsiblePerson.asaas_customer_id;
            console.log('Customer_id encontrado na tabela people:', customerId);
          } else {
            console.log('Responsável não possui asaas_customer_id na tabela people');
          }
        } catch (error) {
          console.error('Erro ao buscar dados do responsável:', error);
        }
      } else {
        console.log('Assinatura não possui selected_parent_id');
      }
      
      // Garantir que o responsável financeiro seja cliente do Asaas
      if (!customerId) {
        console.log('🔍 Responsável ainda não é cliente do Asaas, verificando...');
        
        try {
          // Buscar dados completos do responsável financeiro
          const responsiblePersonData = {
            id: student.selected_parent_id,
            name: student.payer_name,
            email: student.payer_email,
            cpf: student.payer_cpf,
            document: student.payer_cpf,
            phone: student.payer_phone,
            asaas_customer_id: null // Será verificado pela função
          };

          const customerResult = await asaasHelpers.ensureCustomerExists(responsiblePersonData, company.id);
          
          customerId = customerResult.customer_id;
          
          if (customerResult.created_new) {
            console.log('✅ Novo cliente criado no Asaas:', customerId);
            toast.success('Cliente criado no Asaas com sucesso!');
          } else {
            console.log('✅ Cliente existente encontrado:', customerId);
          }
          
        } catch (customerError) {
          toast.dismiss(loadingToast);
          toast.error(`Erro ao garantir cliente no Asaas: ${customerError.message}`);
          return;
        }
      }
      
      // Agora integrar as faturas
      let successCount = 0;
      
      for (const invoice of pendingInvoices) {
        try {
          // Preparar dados da cobrança individual
          const paymentData = {
            customer_id: customerId,
            company_id: company.id,
            value: parseFloat(invoice.final_amount),
            description: `${student.monthly_plans?.name || 'Mensalidade'} - ${student.students?.name || student.payer_name}`,
            due_date: invoice.due_date,
            postal_service: false
          };
          
          // Criar cobrança individual no Asaas com retry automático
          const responsiblePersonData = {
            id: student.selected_parent_id,
            name: student.payer_name,
            email: student.payer_email,
            cpf: student.payer_cpf,
            phone: student.payer_phone
          };
          
          const paymentResult = await asaasHelpers.createBoletoPaymentWithRetry(
            paymentData, 
            responsiblePersonData, 
            company.id
          );
          
          // Atualizar fatura com dados do Asaas
          if (paymentResult.success && paymentResult.payment) {
            await dataHelpers.updateMonthlyInvoice(invoice.id, {
              asaas_payment_id: paymentResult.payment.id,
              asaas_invoice_url: paymentResult.invoice_url,
              asaas_status: paymentResult.payment.status
            });
            
            successCount++;
            console.log(`Fatura integrada com sucesso:`, paymentResult.payment.id);
          } else {
            throw new Error(paymentResult.error || 'Erro na resposta do Asaas');
          }
        } catch (invoiceError) {
          console.error(`Erro ao integrar fatura ${invoice.installment_number}:`, invoiceError);
        }
      }
      
      toast.dismiss(loadingToast);
      
      if (successCount === pendingInvoices.length) {
        toast.success(`${successCount} faturas integradas com Asaas com sucesso!`);
      } else if (successCount > 0) {
        toast.success(`${successCount} de ${pendingInvoices.length} faturas integradas com sucesso!`);
      } else {
        toast.error('Nenhuma fatura foi integrada. Verifique os logs para mais detalhes.');
      }
      
      await loadData();
      
      // Atualizar faturas no modal se estiver aberto
      if (selectedStudentInvoices && selectedStudentInvoices.id) {
        const updatedInvoices = await dataHelpers.getMonthlyInvoices(company.id);
        const subscriberInvoices = updatedInvoices.filter(inv => 
          inv.monthly_subscriber_id === selectedStudentInvoices.id
        );
        setSelectedStudentInvoices(prev => ({
          ...prev,
          invoices: subscriberInvoices
        }));
      }
      
    } catch (error) {
      console.error('Error integrating with Asaas:', error);
      toast.dismiss(loadingToast);
      toast.error('Erro ao integrar com Asaas: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvoice = async (invoice, student) => {
    // Verificar se a fatura está paga
    if (invoice.status === 'paid') {
      toast.error('Não é possível excluir uma fatura que já foi paga');
      return;
    }
    
    // Usar o modal de confirmação existente
    setDeleteTarget({
      type: 'invoice',
      id: invoice.id,
      name: `Parcela ${invoice.installment_number}/${invoice.total_installments}`,
      warning: invoice.asaas_payment_id ? 'Esta fatura também será excluída do Asaas.' : 'Esta ação não pode ser desfeita.',
      confirmAction: async () => {
        try {
          setLoading(true);
          
          // Se a fatura tem integração com Asaas, excluir do Asaas primeiro
          if (invoice.asaas_payment_id) {
            console.log('🗑️ Excluindo fatura do Asaas:', invoice.asaas_payment_id);
            
            try {
              const deleteResult = await asaasHelpers.deletePayment(invoice.asaas_payment_id, company.id);
              
              if (!deleteResult.success) {
                console.warn('⚠️ Erro ao excluir do Asaas:', deleteResult.error);
                toast.error(`Aviso: Erro ao excluir do Asaas: ${deleteResult.error}. A fatura será excluída apenas localmente.`);
              } else {
                console.log('✅ Fatura excluída do Asaas com sucesso');
              }
            } catch (asaasError) {
              console.warn('⚠️ Erro na comunicação com Asaas:', asaasError);
              toast.error('Aviso: Erro na comunicação com Asaas. A fatura será excluída apenas localmente.');
            }
          }
          
          // Excluir fatura do sistema local
          await dataHelpers.deleteMonthlyInvoice(invoice.id);
          
          toast.success(`Fatura ${invoice.installment_number}/${invoice.total_installments} excluída com sucesso!`);
          
          // Recarregar dados
          await loadData();
          
          // Atualizar faturas no modal
          if (student.id) {
            const data = await dataHelpers.getMonthlyInvoices(company.id);
            const subscriberInvoices = data.filter(inv => 
              inv.monthly_subscriber_id === student.id
            );
            setSelectedStudentInvoices(prev => ({
              ...prev,
              invoices: subscriberInvoices
            }));
          }
          
        } catch (error) {
          console.error('Error deleting invoice:', error);
          toast.error('Erro ao excluir fatura: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    });
    setShowDeleteModal(true);
  };

  const handleSendResponsavelSummary = async (responsavel) => {
    try {
      // Implementar envio de resumo por email
      toast.success(`Resumo enviado para ${responsavel.payer_name}`);
    } catch (error) {
      console.error('Error sending summary:', error);
      toast.error('Erro ao enviar resumo');
    }
  };

  const handleExportResponsavelReport = async (responsavel) => {
    try {
      const reportData = {
        responsavel,
        periodo: new Date().toISOString().slice(0, 7)
      };
      
      const dataStr = JSON.stringify(reportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-${responsavel.payer_name.replace(/\s+/g, '-')}.json`;
      link.click();
      
      toast.success('Relatório do responsável exportado!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Erro ao gerar relatório');
    }
  };

  const renderMetricsCards = () => (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
      <Card className="p-4">
        <div className="flex items-center">
          <div className="p-2 rounded-lg bg-blue-100 mr-3">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600">Responsáveis</p>
            <p className="text-xl font-bold text-gray-900">{metrics.totalResponsaveis}</p>
            <p className="text-xs text-gray-500">{metrics.alunosAtivos} ativos</p>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center">
          <div className="p-2 rounded-lg bg-green-100 mr-3">
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600">Receita Mensal</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(metrics.receitaMensal)}</p>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center">
          <div className="p-2 rounded-lg bg-orange-100 mr-3">
            <TrendingUp className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600">Valor Pendente</p>
            <p className="text-xl font-bold text-orange-600">{formatCurrency(metrics.valorTotalPendente)}</p>
            <p className="text-xs text-gray-500">
              {formatCurrency(metrics.valorPendente)} + {formatCurrency(metrics.valorVencido)}
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center">
          <div className="p-2 rounded-lg bg-yellow-100 mr-3">
            <Clock className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600">Pendentes</p>
            <p className="text-xl font-bold text-yellow-600">{metrics.faturasPendentes}</p>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center">
          <div className="p-2 rounded-lg bg-red-100 mr-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600">Vencidas</p>
            <p className="text-xl font-bold text-red-600">{metrics.faturasVencidas}</p>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderStudentCard = (student) => {
    const statusConfig = getStatusConfig(student.status);
    const StatusIcon = statusConfig.icon;
    
    return (
      <div key={student.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2">
              <UserCheck className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm">{student.students?.name}</h4>
              <p className="text-xs text-gray-600">
                {student.students?.registration_number} • {student.students?.class_name}
              </p>
            </div>
          </div>
          <span className={`px-2 py-1 text-xs rounded-full ${statusConfig.color}`}>
            {statusConfig.text}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-2">
          <div>
            <p className="text-xs text-gray-600">Plano</p>
            <p className="font-medium text-sm">{student.monthly_plans?.name}</p>
            <p className="text-xs text-gray-500">{formatCurrency(student.monthly_plans?.monthly_value)}/mês</p>
            <p className="text-xs text-blue-600 font-medium">
              {student.invoices.length}/{student.monthly_plans?.installments || 1} parcelas
            </p>
            {student.invoices.length === (student.monthly_plans?.installments || 1) && (
              <p className="text-xs text-green-600 font-medium">
                ✅ Todas as faturas geradas
              </p>
            )}
            {student.payer_name && student.payer_email && student.payer_cpf ? (
              student.asaas_customer_id ? (
                student.invoices.some(inv => inv.asaas_payment_id) ? (
                  <p className="text-xs text-green-600 font-medium flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                    Asaas integrado
                  </p>
                ) : (
                  <p className="text-xs text-blue-600 flex items-center">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-1"></div>
                    Pronto para Asaas
                  </p>
                )
              ) : (
                <p className="text-xs text-gray-500 flex items-center">
                  <div className="w-2 h-2 bg-gray-400 rounded-full mr-1"></div>
                  Sem integração
                </p>
              )
            ) : (
              <p className="text-xs text-red-600 font-medium flex items-center">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
                Dados incompletos
              </p>
            )}
          </div>
          <div className="grid grid-cols-4 gap-1 text-center">
            <div className="bg-green-50 rounded p-1">
              <div className="text-sm font-bold text-green-600">{student.paid_invoices}</div>
              <div className="text-xs text-gray-600">Pagas</div>
            </div>
            <div className="bg-yellow-50 rounded p-1">
              <div className="text-sm font-bold text-yellow-600">{student.pending_invoices}</div>
              <div className="text-xs text-gray-600">Pend.</div>
            </div>
            <div className="bg-red-50 rounded p-1">
              <div className="text-sm font-bold text-red-600">{student.overdue_invoices}</div>
              <div className="text-xs text-gray-600">Venc.</div>
            </div>
            <div className="bg-blue-50 rounded p-1">
              <div className="text-sm font-bold text-blue-600">
                {student.invoices.filter(inv => inv.viewed_at).length}
              </div>
              <div className="text-xs text-gray-600 flex items-center justify-center">
                <Eye className="w-2 h-2 mr-1" />
                Vistas
              </div>
            </div>
          </div>
        </div>

        {/* Ações */}
        <div className="flex justify-end space-x-1 pt-2 border-t border-gray-200">
          <Button 
            size="sm" 
            variant="ghost" 
            title="Editar assinatura"
            onClick={() => handleEditSubscriber(student)}
          >
            <Edit className="w-3 h-3" />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            title="Ver faturas"
            onClick={() => handleViewInvoices(student)}
          >
            <FileText className="w-3 h-3" />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            title="Enviar cobrança"
            onClick={() => handleSendNotification(student)}
          >
            <Send className="w-3 h-3" />
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            title="Excluir assinatura"
            onClick={() => handleDeleteSubscriber(student)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
    );
  };

  const renderResponsavelCard = (responsavel) => {
    const isExpanded = expandedCards[responsavel.payer_email];
    const totalPendingInvoices = responsavel.students.reduce((sum, student) => 
      sum + student.pending_invoices + student.overdue_invoices, 0
    );

    return (
      <Card key={responsavel.payer_email} className="overflow-hidden">
        <div 
          className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleCardExpansion(responsavel.payer_email)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{responsavel.payer_name}</h3>
                <p className="text-gray-600">{responsavel.payer_email}</p>
                {responsavel.payer_phone && (
                  <p className="text-sm text-gray-500">{responsavel.payer_phone}</p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="text-sm text-gray-600">
                  {responsavel.students.length} aluno{responsavel.students.length !== 1 ? 's' : ''}
                </div>
                <div className="text-lg font-bold text-green-600">
                  {formatCurrency(responsavel.total_monthly)}
                </div>
                {totalPendingInvoices > 0 && (
                  <div className="text-sm text-red-600">
                    {totalPendingInvoices} pendente{totalPendingInvoices !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
              
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>
        </div>

        {isExpanded && (
          <div className="px-6 pb-6 space-y-4">
            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Alunos e Assinaturas</h4>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {responsavel.students.map(student => renderStudentCard(student))}
              </div>
            </div>

            {/* Ações do responsável */}
            <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setShowSubscriberModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Assinatura
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleSendResponsavelSummary(responsavel)}
              >
                <Mail className="w-4 h-4 mr-2" />
                Enviar Resumo
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => handleExportResponsavelReport(responsavel)}
              >
                <Download className="w-4 h-4 mr-2" />
                Relatório
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Controle de Mensalidades</h1>
        <p className="text-gray-600">Gerencie assinaturas por responsável e acompanhe o financeiro por aluno</p>
      </div>

      {renderMetricsCards()}

      {/* Ações rápidas */}
      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Ações Rápidas</h3>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={() => setShowPlansManager(true)}>
              <CreditCard className="w-4 h-4 mr-1" />
              Gerenciar Planos
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowSubscriberModal(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Nova Assinatura
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportReport}>
              <Download className="w-4 h-4 mr-1" />
              Relatório
            </Button>
          </div>
        </div>
      </Card>

      {/* Filtro de busca */}
      <Card className="p-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar por responsável, email ou nome do aluno..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 py-2"
          />
        </div>
      </Card>

      {/* Lista de responsáveis */}
      <div className="space-y-4">
        {loading ? (
          <Card className="p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Carregando...</p>
          </Card>
        ) : filteredResponsaveis.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchTerm ? 'Nenhum resultado encontrado' : 'Nenhuma assinatura cadastrada'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm ? 'Tente ajustar os termos da busca' : 'Comece criando a primeira assinatura de mensalidade'}
            </p>
            {!searchTerm && (
              <Button onClick={() => setShowSubscriberModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeira Assinatura
              </Button>
            )}
          </Card>
        ) : (
          filteredResponsaveis.map(responsavel => renderResponsavelCard(responsavel))
        )}
      </div>

      {/* Modal de Gerenciamento de Planos */}
      {showPlansManager && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-medium text-gray-900">Gerenciar Planos de Mensalidade</h3>
              <div className="flex space-x-2">
                <Button onClick={() => setShowPlanModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Novo Plano
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowPlansManager(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="overflow-y-auto max-h-96">
              {data.plans.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Nenhum plano cadastrado</h4>
                  <p className="text-gray-600 mb-4">Crie seu primeiro plano de mensalidade</p>
                  <Button onClick={() => setShowPlanModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Primeiro Plano
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.plans.map((plan) => {
                    const subscribersCount = data.subscribers.filter(s => s.monthly_plan_id === plan.id).length;
                    
                    return (
                      <Card key={plan.id} className="p-4 hover:shadow-lg transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">{plan.name}</h4>
                            {plan.description && (
                              <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                            )}
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            plan.active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {plan.active ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>

                        <div className="space-y-2 mb-4">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Valor Total:</span>
                            <span className="font-medium">{formatCurrency(plan.total_value)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Parcelas:</span>
                            <span className="font-medium">{plan.installments}x de {formatCurrency(plan.monthly_value)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Vencimento:</span>
                            <span className="font-medium">Dia {plan.due_day}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Assinantes:</span>
                            <span className="font-medium">{subscribersCount}</span>
                          </div>
                        </div>

                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleEditPlan(plan)}
                            className="flex-1"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Editar
                          </Button>                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleDeletePlan(plan.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            disabled={subscribersCount > 0}
                            title={subscribersCount > 0 ? 'Não é possível excluir plano com assinantes' : 'Excluir plano'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Plano */}
      {showPlanModal && (
        <MonthlyPlanForm
          onSubmit={handlePlanSubmit}
          onCancel={() => {
            setShowPlanModal(false);
            setEditingPlan(null);
          }}
          initialData={editingPlan}
          loading={loading}
        />
      )}

      {/* Modal de Assinatura */}
      {showSubscriberModal && (
        <MonthlySubscriberForm
          onSubmit={handleSubscriberSubmit}
          onCancel={() => {
            setShowSubscriberModal(false);
            setEditingSubscriber(null);
          }}
          initialData={editingSubscriber}
          loading={loading}
        />
      )}

      {/* Modal de Faturas - Design Renovado */}
      {showInvoicesModal && selectedStudentInvoices && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden border border-gray-200">
            {/* Header Moderno */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">
                      Faturas de {selectedStudentInvoices.students?.name}
                    </h3>
                    <p className="text-blue-100 text-sm">
                      {selectedStudentInvoices.invoices.length} faturas • 
                      {selectedStudentInvoices.invoices.filter(inv => inv.status === 'pending').length} pendentes
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {selectedStudentInvoices.invoices.filter(inv => !inv.asaas_payment_id && inv.status === 'pending').length > 0 && (
                    <Button
                      size="sm"
                      className="bg-white/20 border-white/30 text-white hover:bg-white/30 backdrop-blur-sm"
                      onClick={() => handleIntegrateWithAsaas(selectedStudentInvoices)}
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      Integrar Pendentes
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/20"
                    onClick={() => {
                      setShowInvoicesModal(false);
                      setSelectedStudentInvoices(null);
                    }}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Resumo de Status */}
            <div className="px-8 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-4 gap-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">
                    {selectedStudentInvoices.invoices.filter(inv => inv.status === 'paid').length} Pagas
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">
                    {selectedStudentInvoices.invoices.filter(inv => inv.status === 'pending').length} Pendentes
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">
                    {selectedStudentInvoices.invoices.filter(inv => inv.status === 'overdue').length} Vencidas
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">
                    {selectedStudentInvoices.invoices.filter(inv => inv.asaas_payment_id).length} Integradas
                  </span>
                </div>
              </div>
            </div>
            
            {/* Tabela com Design Moderno */}
            <div className="overflow-y-auto max-h-[50vh] px-8 py-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Parcela</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Vencimento</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Valor</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Status</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Vista</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Pagamento</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Data Pgto</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700 text-sm">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedStudentInvoices.invoices.map(invoice => {
                      const statusConfig = getStatusConfig(invoice.status);
                      const isIntegrated = !!invoice.asaas_payment_id;
                      const canIntegrate = !isIntegrated && invoice.status === 'pending';
                      const canDelete = invoice.status !== 'paid';
                      
                      
                      return (
                        <tr key={invoice.id} className="hover:bg-blue-50/50 transition-colors duration-200">
                          <td className="py-4 px-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <span className="text-blue-600 font-semibold text-sm">
                                  {invoice.installment_number || 1}
                                </span>
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  Parcela {invoice.installment_number || 1}/{invoice.total_installments || 1}
                                </div>
                                {invoice.installment_number === 1 && (
                                  <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full mt-1">
                                    Primeira parcela
                                  </span>
                                )}
                                {isIntegrated && (
                                  <span className="inline-block px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full mt-1">
                                    🔗 Asaas
                                  </span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-gray-900 font-medium">
                              {formatDate(invoice.due_date)}
                            </div>
                            <div className="text-gray-500 text-sm">
                              {new Date(invoice.due_date).toLocaleDateString('pt-BR', { weekday: 'short' })}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <div className="text-gray-900 font-bold">
                              {formatCurrency(invoice.final_amount)}
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${statusConfig.color}`}>
                              <statusConfig.icon className="w-4 h-4 mr-1" />
                              {statusConfig.text}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <div className="flex justify-center">
                              {renderViewedIcon(invoice)}
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <div className="flex justify-center">
                              {renderPaymentStatus(invoice)}
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center text-sm text-gray-700">
                            {invoice.paid_at ? formatDate(invoice.paid_at) : '-'}
                          </td>
                          <td className="py-4 px-4">
                            <div className="flex items-center justify-center space-x-1">
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                title="Enviar por email"
                                className="p-2 hover:bg-blue-100 rounded-lg"
                              >
                                <Mail className="w-4 h-4 text-gray-600" />
                              </Button>
                              
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                title="Ver detalhes"
                                onClick={() => openInvoiceDetails(invoice)}
                                className="p-2 hover:bg-green-100 rounded-lg"
                              >
                                <Eye className="w-4 h-4 text-gray-600" />
                              </Button>
                              
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                title={canIntegrate ? "Integrar com Asaas" : "Já integrado com Asaas"}
                                onClick={canIntegrate ? () => handleIntegrateInvoiceWithAsaas(invoice, selectedStudentInvoices) : undefined}
                                disabled={!canIntegrate}
                                className={`p-2 rounded-lg transition-all duration-200 ${
                                  canIntegrate 
                                    ? 'hover:bg-blue-100 text-blue-600 cursor-pointer' 
                                    : 'bg-green-50 text-green-600 cursor-not-allowed opacity-75'
                                }`}
                              >
                                {canIntegrate ? (
                                  <Building2 className="w-4 h-4" />
                                ) : (
                                  <BadgeCheck className="w-4 h-4" />
                                )}
                              </Button>
                              
                              {canDelete && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  title="Excluir fatura"
                                  onClick={() => handleDeleteInvoice(invoice, selectedStudentInvoices)}
                                  className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Footer com estatísticas */}
            <div className="px-8 py-6 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Total:</span> {selectedStudentInvoices.invoices.length} faturas
                  </div>
                  <div>
                    <span className="font-medium">Valor Total:</span> {formatCurrency(
                      selectedStudentInvoices.invoices.reduce((sum, inv) => sum + parseFloat(inv.final_amount || 0), 0)
                    )}
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowInvoicesModal(false);
                    setSelectedStudentInvoices(null);
                  }}
                  className="px-6 py-2"
                >
                  Fechar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Confirmar Exclusão
                </h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-3">
                {deleteTarget.type === 'plan' && (
                  <>Tem certeza que deseja excluir o plano <strong>"{deleteTarget.name}"</strong>?</>
                )}
                {deleteTarget.type === 'subscriber' && (
                  <>Tem certeza que deseja excluir a assinatura de <strong>"{deleteTarget.name}"</strong>?</>
                )}
                {deleteTarget.type === 'invoice' && (
                  <>Tem certeza que deseja excluir a fatura <strong>"{deleteTarget.name}"</strong>?</>
                )}
              </p>
              
              {deleteTarget.warning && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-3">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        {deleteTarget.warning}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-500">
                Esta ação não pode ser desfeita.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={cancelDelete}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmDelete}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={loading}
              >
                {loading ? 'Excluindo...' : 'Excluir'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyPaymentsPage;